import React from "react";
import _ from "lodash";
import styles from "./Users.module.scss";
import gql from "graphql-tag";
import { useMutation } from "@apollo/client";
import { TableRow, TableCell, MenuItem, Tooltip } from "@material-ui/core";
import UserAvatars from "../../components/UserAvatars/UserAvatars";
import Menu from "../../components/Menu/Menu";
import Icon from "@mdi/react";
import { mdiCreditCardOutline, mdiBellOutline, mdiBellOffOutline } from "@mdi/js";

const User = ({ user, snack, handleEdit, handleDelete, handleDeleteImage, fiscalYear, disableDelete, isAdmin }) => {
  const { id, name, profilePicture, position, email, auth, plan, stripeCustomerId, notificationSettings } = user;

  const [sendPasswordResetEmail] = useMutation(SEND_PASSWORD_RESET_EMAIL);

  const handlePasswordReset = async (skipEmail = false) => {
    try {
      const res = await sendPasswordResetEmail({ variables: { email, skipEmail } });
      const pwResetUrl = _.get(res, "data.sendPasswordResetEmail");

      let clipboardSuccess = false;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(pwResetUrl);
          clipboardSuccess = true;
        } catch (clipErr) {
          console.warn("Failed to copy to clipboard:", clipErr);
        }
      } else {
        console.warn("Clipboard API not supported");
      }

      if (skipEmail) {
        if (clipboardSuccess) {
          snack("Password reset link generated and copied to clipboard");
        } else {
          snack(`Password reset link generated: ${pwResetUrl}`);
        }
      } else {
        if (clipboardSuccess) {
          snack(`Password reset link sent to ${email} and copied to clipboard`);
        } else {
          snack(`Password reset link sent to ${email}: ${pwResetUrl}`);
        }
      }
    } catch (err) {
      snack(err.message, "error");
    }
  };

  const currentYearPlans = plan.filter((p) => p.year === fiscalYear);
  const isPayingUser = Boolean(stripeCustomerId);

  const renderNotificationStatus = (notificationType) => {
    const isEnabled = _.get(notificationSettings, `${notificationType}.enabled`, false);
    const frequency = _.get(notificationSettings, `${notificationType}.frequency`, "N/A");

    return (
      <Tooltip title={isEnabled ? `Frequency: ${_.startCase(frequency)}` : "Notifications disabled"}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            color: isEnabled ? "#2e7d32" : "#757575",
            fontWeight: isEnabled ? 500 : 400,
          }}
        >
          <Icon path={isEnabled ? mdiBellOutline : mdiBellOffOutline} size={0.7} style={{ marginRight: 4 }} />
          {isEnabled ? "On" : "Off"}
        </div>
      </Tooltip>
    );
  };

  return (
    <>
      <TableRow>
        <TableCell className={styles.firstCell}>
          <UserAvatars users={[{ id, name, profilePicture }]} />
        </TableCell>
        <TableCell>
          <div className={styles.flex}>
            {name.first} {name.last}
            {isPayingUser && (
              <Tooltip title="Paying user">
                <span style={{ marginLeft: 8 }}>
                  <Icon path={mdiCreditCardOutline} size={0.7} color="#4caf50" style={{ verticalAlign: "middle" }} />
                </span>
              </Tooltip>
            )}
            <Menu>
              <MenuItem onClick={handleEdit}>Edit</MenuItem>
              <MenuItem onClick={() => handlePasswordReset(false)}>Send Password Reset Link</MenuItem>
              {isAdmin && <MenuItem onClick={() => handlePasswordReset(true)}>Generate Reset Link</MenuItem>}
              {profilePicture && (
                <MenuItem onClick={handleDeleteImage} className={styles.delete}>
                  Delete Profile Picture
                </MenuItem>
              )}
              <MenuItem onClick={handleDelete} className={styles.delete} disabled={disableDelete}>
                Delete
              </MenuItem>
            </Menu>
          </div>
        </TableCell>
        <TableCell>{position}</TableCell>
        <TableCell>
          <a href={`mailto:${email}`} className={styles.email}>
            {email}
          </a>
        </TableCell>
        <TableCell>
          {currentYearPlans.length === 0 ? (
            <span
              style={{
                color: "#9e9e9e",
                fontStyle: "italic",
                fontSize: "0.8rem",
              }}
            >
              No Departments
            </span>
          ) : currentYearPlans.length <= 2 ? (
            <div>
              {currentYearPlans.map((plan) => (
                <div
                  key={plan.id}
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    display: "inline-block",
                    marginBottom: "2px",
                    marginRight: "2px",
                    fontSize: "0.8rem",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {plan.departmentName}
                </div>
              ))}
            </div>
          ) : (
            <Tooltip
              title={currentYearPlans.map(({ departmentName }, idx) => departmentName + (idx === currentYearPlans.length - 1 ? "" : ", "))}
            >
              <div>
                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    display: "inline-block",
                    marginBottom: "2px",
                    fontSize: "0.8rem",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {currentYearPlans[0].departmentName}
                </div>
                <div style={{ color: "#757575", fontSize: "0.75rem", marginTop: "2px" }}>+{currentYearPlans.length - 1} more</div>
              </div>
            </Tooltip>
          )}
        </TableCell>
        <TableCell className={styles.auth}>
          <div className={styles.flex}>{auth}</div>
        </TableCell>
        <TableCell>{renderNotificationStatus("weeklyReminder")}</TableCell>
        <TableCell>{renderNotificationStatus("meetingReminder")}</TableCell>
      </TableRow>
    </>
  );
};

export default User;

const SEND_PASSWORD_RESET_EMAIL = gql`
  mutation ($email: String, $_id: ID, $skipEmail: Boolean) {
    sendPasswordResetEmail(email: $email, _id: $_id, skipEmail: $skipEmail)
  }
`;
